<template>
  <EditorTabFrame
    :loading="loading"
    :loaded="loaded"
    :load-error="loadError"
    :saving="saving"
    :dirty="isDirty"
    :conflict-message="conflictMessage"
    @save="save"
    @discard="discard"
    @reload="load"
  >
    <div v-if="form">
      <EditorSection
        title="Termo de anuência"
        description="Texto que o doador aceita no checkout, por meio de pagamento. Em branco, vale o texto padrão da plataforma."
      >
        <template slot="aside">
          <span class="ed-status-pill" :class="{ 'ed-status-pill--on': form.terms.some(hasText) }">
            {{ form.terms.some(hasText) ? "Texto próprio" : "Texto padrão" }}
          </span>
        </template>

        <v-expansion-panel expand class="elevation-0 ed-terms">
          <v-expansion-panel-content v-for="term in form.terms" :key="term.paymentMethodId">
            <div slot="header" class="ed-terms__head">
              <strong>{{ term.paymentMethodName }}</strong>
              <span class="ed-help">{{ hasText(term) ? "texto próprio" : "texto padrão" }}</span>
            </div>
            <div class="ed-terms__body">
              <VueEditor v-model="term.text" :editorToolbar="toolbar" />
              <div v-if="term.legacyText && !hasText(term)" class="mt-2">
                <v-btn small flat color="primary" class="ed-text-btn px-0" @click="term.text = term.legacyText">Usar o texto do checkout antigo</v-btn>
              </div>
              <details v-if="term.defaultText" class="mt-2">
                <summary class="ed-help">Ver o texto padrão</summary>
                <div class="ed-help mt-1" v-html="term.defaultText"></div>
              </details>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </EditorSection>

      <EditorSection title="LGPD" description="Consentimento para uso de dados pessoais, conforme a Lei Geral de Proteção de Dados.">
        <EditorSwitch
          :value="form.enableLGPD"
          label="Pedir consentimento LGPD nesta campanha"
          :hint="companyEnableLGPD ? 'A empresa dona tem LGPD habilitada.' : 'A empresa dona não tem LGPD habilitada.'"
          @input="onLgpdChange"
        />
        <div v-if="form.enableLGPD" class="mt-3">
          <label class="ed-help d-block mb-1">
            Texto da modal LGPD (aceita HTML). Preenchido: a modal usa este texto e o aceite vale só para esta campanha.
            Vazio: usa o texto da empresa.
          </label>
          <textarea
            v-model="form.lgpdText"
            rows="8"
            placeholder="<p>Você aceita participar da promoção?</p>"
            style="width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: monospace; font-size: 13px;"
          ></textarea>
          <details v-if="form.lgpdText.trim()" class="mt-2" open>
            <summary class="ed-help">Pré-visualização</summary>
            <div class="mt-1" v-html="form.lgpdText" style="border: 1px dashed #ccc; border-radius: 4px; padding: 12px; max-height: 300px; overflow-y: auto;"></div>
          </details>
          <v-layout row wrap class="mt-2">
            <v-flex xs12 md6 class="pr-2">
              <v-text-field v-model="form.lgpdAcceptText" label="Texto da opção de aceite" placeholder="Sim, autorizo." maxlength="200" />
            </v-flex>
            <v-flex xs12 md6 class="pl-2">
              <v-text-field v-model="form.lgpdDeclineText" label="Texto da opção de recusa" placeholder="Não autorizo." maxlength="200" />
            </v-flex>
          </v-layout>
          <v-checkbox
            v-model="form.lgpdSkipConfirmation"
            label="Gravar a escolha direto, sem perguntar &quot;Você confirma que autoriza?&quot;"
            hide-details
          />
        </div>
      </EditorSection>
    </div>
  </EditorTabFrame>
</template>

<script>
import { VueEditor } from "vue2-editor";
import editorTab from "../editorTab.mixin";
import EditorTabFrame from "../EditorTabFrame.vue";
import EditorSection from "../EditorSection.vue";
import EditorSwitch from "../EditorSwitch.vue";
import CampaignEditorService from "@/scripts/services/campaignEditor.service";

const service = new CampaignEditorService();

export default {
  name: "TermsTab",
  mixins: [editorTab],
  components: { EditorSection, VueEditor, EditorTabFrame, EditorSwitch },
  data() {
    return {
      companyEnableLGPD: false,
      toolbar: Object.freeze([["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }]]),
    };
  },
  methods: {
    fetchSection() {
      return service.getTerms(this.campaignId);
    },
    saveSection(body) {
      return service.saveTerms(body);
    },
    toForm(dto) {
      return {
        enableLGPD: !!dto.enableLGPD,
        lgpdText: dto.lgpdText || "",
        lgpdAcceptText: dto.lgpdAcceptText || "",
        lgpdDeclineText: dto.lgpdDeclineText || "",
        lgpdSkipConfirmation: !!dto.lgpdSkipConfirmation,
        terms: (dto.consentTerms || []).map((term) => ({
          paymentMethodId: term.paymentMethodId,
          paymentMethodName: term.paymentMethodName,
          text: term.text || "",
          defaultText: term.defaultText,
          legacyText: term.legacyText,
        })),
      };
    },
    afterLoad(dto) {
      this.companyEnableLGPD = !!dto.companyEnableLGPD;
      // O editor rico (Quill) normaliza o HTML ao montar; refaz o snapshot depois disso
      // para não acusar alteração que o usuário não fez.
      setTimeout(() => {
        if (this.form) this.snapshot = JSON.stringify(this.form);
      }, 600);
    },
    hasText(term) {
      return !!(term.text || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    },
    onLgpdChange(value) {
      // A tela antiga pedia confirmação para habilitar/desabilitar.
      if (window.confirm("Você tem certeza que deseja habilitar/desabilitar o termo de consentimento (LGPD) para a campanha?")) {
        this.form.enableLGPD = value;
      }
    },
    buildPayload() {
      return {
        enableLGPD: this.form.enableLGPD,
        lgpdText: this.form.lgpdText,
        lgpdAcceptText: this.form.lgpdAcceptText,
        lgpdDeclineText: this.form.lgpdDeclineText,
        lgpdSkipConfirmation: this.form.lgpdSkipConfirmation,
        consentTerms: this.form.terms.map((term) => ({ paymentMethodId: term.paymentMethodId, text: term.text })),
      };
    },
  },
};
</script>
